import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch } from "react-router";

import { useURLSearchParamsList } from "widgets/hooks";

import { DEFAULT_BED_TRACK_CONFIGURATION } from "../bed-track/BedsTrackCustomizationDialog.js";
import {
  DEFAULT_NGS_COVERAGE_CONFIGURATION,
  DEFAULT_NGS_TRACK_CONFIGURATION,
} from "../ngs-track/NgsTrackCustomizationDialog.js";
import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import {
  type GenomeBrowserStatistics,
  type InputTableQuerySpec,
  type Organism,
  type Track,
  type TrackBedConfiguration,
  type TrackCustomization,
  type TrackNgsConfiguration,
} from "@/graphql.ts";
import {
  type ISortableTrack,
  fetchGenomeBrowserStatistics,
  getAllTracks,
  getTableSpecFromSortableTrack,
  selectActiveFeaturesTable,
  setTrackList,
} from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { TRACK_TYPE, UNKNOWN_GENOME_ID, VIEW } from "@/settings/settings.ts";
import { DEFAULT_COORDINATE_TRACK_CONFIGURATION } from "@/ui-modules/genome-viewer/coordinate-track/CoordinatesTrackCustomizationDialog.tsx";

export const useBamFileIds = () => {
  const ngsConfiguration = useSelector<IAppState, TrackNgsConfiguration>((state) => {
    const ngsTrack = state.genomeBrowser.tracks
      .filter((tr) => !tr.hidden) // if the NGS track gets hidden then we don't consider the configured BAM files
      .find((tr) => tr.type === TRACK_TYPE.NGS);
    return ngsTrack?.trackCustomization?.ngsConfiguration;
  });
  return useMemo(
    () => ngsConfiguration?.orderedBamFiles?.map((bamFileData) => bamFileData?.bamFileId),
    [ngsConfiguration],
  );
};

export const useBedFileIds = () => {
  const bedConfiguration = useSelector<IAppState, TrackBedConfiguration>((state) => {
    const bedTrack = state.genomeBrowser.tracks
      .filter((tr) => !tr.hidden) // if the BED track gets hidden then we don't consider the configured BED files
      .find((tr) => tr.type === TRACK_TYPE.BED);
    return bedTrack?.trackCustomization?.bedConfiguration;
  });
  return useMemo(
    () => bedConfiguration?.orderedBedFiles?.map((bedFileData) => bedFileData?.bedFileId),
    [bedConfiguration],
  );
};

export const useUpdateGenomeViewerTracks = (defaultTracks: ISortableTrack[]) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const viewId = useURLViewID();
  const isGenomeViewType = useSelector<IAppState, boolean>(
    (state) => state.savedView?.view?.viewType === VIEW.GENOME_VIEW.type,
  );
  // load default set of tracks if viewId does not correspond to a genome view, e.g. when coming to the
  // genome viewer from a transcript table view (see SEL10-1311)
  const initialSetup = useRef<boolean>(!viewId || (viewId && !isGenomeViewType));
  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome);
  const existingTracks = useSelector<IAppState, ISortableTrack[]>((state) => state.genomeBrowser.tracks);
  const defaultTracksLayout = useSelector<IAppState, Track[]>((state) => state.auth.session.params.defaultTracks);

  // pre-configuration via query parameters will be set only once
  const addTracksAsStrings = useURLSearchParamsList("add_tracks");
  const BAMFileIdsAsStrings = useURLSearchParamsList("bam_files");
  const BEDFileIdsAsStrings = useURLSearchParamsList("bed_files");
  const coordinateListIdsAsStrings = useURLSearchParamsList("coordLists");

  const jobContext = useMatch("/job/:jobId/*");

  const { additionalTracks, BAMFileIds, BEDFileIds, coordinateListIds } = useMemo(() => {
    const additionalTracks: Array<Partial<Track>> = addTracksAsStrings?.map((key) => {
      const [type, subTypeId] = key.split("@");
      return {
        type: type,
        subTypeId: subTypeId && Number(subTypeId),
      };
    });
    const BAMFileIds: number[] = BAMFileIdsAsStrings?.map((s) => Number.parseInt(s, 10));
    const BEDFileIds: number[] = BEDFileIdsAsStrings?.map((s) => Number.parseInt(s, 10));
    const coordinateListIds: number[] = coordinateListIdsAsStrings?.map((s) => Number.parseInt(s, 10));
    return { additionalTracks, BAMFileIds, BEDFileIds, coordinateListIds };
  }, []);

  useEffect(() => {
    initialSetup.current = !viewId || (viewId && !isGenomeViewType);
  }, [viewId, isGenomeViewType]);

  // initialize the active table when the tracks are provided via query params
  useEffect(() => {
    if (additionalTracks) {
      const activeTrack = additionalTracks[additionalTracks.length - 1];
      let activeTableSpec: InputTableQuerySpec = null;
      if (activeTrack.type === TRACK_TYPE.BED && BEDFileIds?.length) {
        activeTableSpec = getTableSpecFromSortableTrack(activeTrack, {
          fileId: BEDFileIds[BEDFileIds.length - 1],
        }); // genome id is not relevant for comparison purpose
      }
      if (activeTrack.type === TRACK_TYPE.COORDINATE && coordinateListIds?.length) {
        activeTableSpec = getTableSpecFromSortableTrack(activeTrack, {
          coordinateListId: coordinateListIds[coordinateListIds.length - 1],
        }); // genome id is not relevant for comparison purpose
      }
      dispatch(selectActiveFeaturesTable(activeTableSpec));
    }
  }, [BEDFileIds, coordinateListIds, additionalTracks]);

  const updateTracksListing = useCallback((genome: Organism, trackListingProps: ITracksListingProps) => {
    void dispatch(fetchGenomeBrowserStatistics(genome?.id)).then((stats: GenomeBrowserStatistics) => {
      // when the statistics might change upon genome or BAM file related sequences modifications, then we don't take the initial query params into consideration

      const tracks = prepareTracksListing(trackListingProps, stats);
      dispatch(setTrackList(tracks));
    });
  }, []);

  useEffect(() => {
    if (genome && genome.id !== UNKNOWN_GENOME_ID) {
      // we make a distinction between the initialization of the genome browser and the following steps
      if (initialSetup.current) {
        initialSetup.current = false;
        // the initialization takes into account the injected query parameters, the default tracks and user registered tracks
        updateTracksListing(genome, {
          preConfiguredTracks: defaultTracks,
          additionalVisibleTracks: additionalTracks,
          defaultTracksLayout,
          BAMFileIds,
          BEDFileIds,
          coordinateListIds,
          jobContext: !!jobContext,
        });
      } else {
        updateTracksListing(genome, {
          preConfiguredTracks: existingTracks.filter((track) => !track.hidden),
          jobContext: !!jobContext,
        });
      }
    }
  }, [genome, jobContext]);
};

interface ITracksListingProps {
  preConfiguredTracks?: ISortableTrack[]; // depend on the summary page context
  additionalVisibleTracks?: Array<Partial<Track>>; // set from the query parameters
  defaultTracksLayout?: Track[];
  BAMFileIds?: number[];
  BEDFileIds?: number[];
  coordinateListIds?: number[];
  jobContext?: boolean;
}

const prepareTracksListing = (
  trackListingProps: ITracksListingProps,
  stats: GenomeBrowserStatistics,
): ISortableTrack[] => {
  const {
    preConfiguredTracks,
    additionalVisibleTracks,
    defaultTracksLayout,
    BAMFileIds,
    BEDFileIds,
    coordinateListIds,
    jobContext,
  } = trackListingProps;
  // Get all tracks, keep them visible if already visible and set them visible from the url too
  const tracks = getAllTracks(preConfiguredTracks, stats, additionalVisibleTracks, defaultTracksLayout, jobContext);

  // Initialize track customization with file or coord list related information
  return tracks?.map((track) => {
    let trackCustomization: TrackCustomization | null = track.trackCustomization;

    if (!track.hidden && track.type === TRACK_TYPE.NGS && BAMFileIds) {
      trackCustomization = {
        ...trackCustomization,
        ngsConfiguration: {
          coverageConfig: DEFAULT_NGS_COVERAGE_CONFIGURATION,
          orderedBamFiles: BAMFileIds.map((bamFileId: number) => ({
            ...DEFAULT_NGS_TRACK_CONFIGURATION,
            bamFileId: bamFileId,
          })),
        },
      };
    }

    if (!track.hidden && track.type === TRACK_TYPE.BED && BEDFileIds) {
      trackCustomization = {
        ...trackCustomization,
        bedConfiguration: {
          orderedBedFiles: BEDFileIds.map((bedFileId: number) => ({
            ...DEFAULT_BED_TRACK_CONFIGURATION,
            bedFileId: bedFileId,
          })),
        },
      };
    }

    if (!track.hidden && track.type === TRACK_TYPE.COORDINATE && coordinateListIds) {
      trackCustomization = {
        ...trackCustomization,
        coordinateConfiguration: {
          orderedCoordinateLists: coordinateListIds.map((coordinateListId: number) => ({
            ...DEFAULT_COORDINATE_TRACK_CONFIGURATION,
            coordinateListId: coordinateListId,
          })),
        },
      };
    }

    return {
      ...track,
      trackCustomization,
    } satisfies ISortableTrack;
  });
};
